import { css } from 'aphrodite';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { FilterOperator, IBulkContactsRequest, ISortDescriptor, KeyDateKind } from '../../../../../extViewmodels';
import { useDebounceCallback } from '../../../../../hooks/useDebounceValue';
import { useAdvancedFiltersContext } from '../../../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import { Button } from '../../../../components-v2/Button';
import { ButtonGroup } from '../../../../components-v2/ButtonGroup';
import { Calendar } from '../../../../components-v2/Calendar';
import { Column } from '../../../../components-v2/PageLayout';
import { KeyFactFiltersDropdown } from '../../../../components/AdvancedFilters/KeyFactFilters';
import { ButtonBase } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/Popover';
import { TagListBuilder } from '../../../../components/entities/tags/TagListBuilder';
import { ChevronDown } from '../../../../components/svgs/icons/ChevronDown';
import { RightChevron } from '../../../../components/svgs/icons/RightChevron';
import { XIconNew } from '../../../../components/svgs/icons/XIcon';
import { bs } from '../../../../styles/styles';
import { CompanyTagFilters } from '../CompanyTagFilters';
import { LocationFilters } from '../LocationFilters';
import { OwnershipFilters } from '../OwnershipFilters';
import { DefaultPeopleFilter, usePeopleSearch } from '../PeopleContext';
import { SavedSearches } from '../SavedSearches';
import { StatusFilterCheckboxes } from '../StatusFilters';
import { styleSheet } from '../styles';
import { LastCommunicationDateDropdown } from './LastCommunicationDateDropdown';
import { PopularTagsV2 } from './PopularTagsV2';
import { compoundFiltersStyles } from './styles';

interface CompoundFiltersProps {
	load: ({ newFilter, newSort }?: { newFilter?: IBulkContactsRequest; newSort?: ISortDescriptor }) => void;
	onClear?: () => void;
}

export const CompoundFilters: FC<CompoundFiltersProps> = ({ load, onClear }) => {
	const { filter } = usePeopleSearch();
	const {
		createDateFilters,
		lastCommunicationDateFilters,
		tagFilters,
		keyFactFilters,
		statusFilters,
		locationFilters,
		companyTagFilters,
		setOwnershipFilter,
		saveChanges,
	} = useAdvancedFiltersContext();

	const [showPopularTags, setShowPopularTags] = useState(true);
	const [showTagFilters, setShowTagFilters] = useState(false);
	const [showSavedSearches, setShowSavedSearches] = useState(true);
	const [showOwnershipFilters, setShowOwnershipFilters] = useState(false);
	const [showStatusFilters, setShowStatusFilters] = useState(false);
	const [showLocationFilters, setShowLocationFilters] = useState(false);
	const [showCompanyTagFilters, setShowCompanyTagFilters] = useState(false);
	const [showCustomDateRange, setShowCustomDateRange] = useState(false);
	const [showLastCommunicationDateRange, setShowLastCommunicationDateRange] = useState(
		!!lastCommunicationDateFilters.startDate ||
			!!lastCommunicationDateFilters.endDate ||
			!!lastCommunicationDateFilters.relativeDate
	);
	const [showKeyFactFilters, setShowKeyFactFilters] = useState(false);

	const debouncedSaveChanges = useDebounceCallback(() => saveChanges());

	const handleStatusFilterChange = (newFilter: IBulkContactsRequest) => {
		statusFilters.refreshStatuses(newFilter.filter.criteria);
		load({ newFilter });
	};

	const handleLocationFilterChange = (newFilter: IBulkContactsRequest) => {
		// Sync location changes to the context so they're preserved when other filters change
		locationFilters.refreshLocationFilters(newFilter.filter.criteria);
		load({ newFilter });
	};

	const handleCompanyTagFilterChange = (newFilter: IBulkContactsRequest) => {
		// Sync company tag changes to the context so they're preserved when other filters change
		companyTagFilters.refreshCompanyTagFilters(newFilter.filter.criteria);
		load({ newFilter });
	};

	const handleOwnershipFilterChange = (newFilter: IBulkContactsRequest) => {
		setOwnershipFilter(newFilter.ownershipFilter);
		load({ newFilter });
	};

	const handleLoadSavedSearch = (newFilter: IBulkContactsRequest) => {
		setOwnershipFilter(newFilter.ownershipFilter);
		statusFilters.refreshStatuses(newFilter.filter.criteria);
		tagFilters.refreshTags(newFilter.filter.criteria);
		keyFactFilters.refreshKeyFacts(newFilter.filter.criteria);
		createDateFilters.refreshDateFilter(newFilter.filter.criteria);
		lastCommunicationDateFilters.refreshDateFilter(newFilter.filter.criteria);
		locationFilters.refreshLocationFilters(newFilter.filter.criteria);
		companyTagFilters.refreshCompanyTagFilters(newFilter.filter.criteria);
		load({ newFilter });
	};

	const handleLiveUpdate = (callback: () => void) => {
		callback();
		debouncedSaveChanges();
	};

	useEffect(() => {
		// Once a key fact is selected, check for data completion on render
		// Update table on render
		if (keyFactFilters.selectedKeyFact) {
			switch (keyFactFilters.selectedKeyFact) {
				case 'AgeRange':
					if (keyFactFilters.selectedMinAge !== null || keyFactFilters.selectedMaxAge !== null) {
						debouncedSaveChanges();
					}
					break;
				case KeyDateKind.Renewal:
					if (
						keyFactFilters.selectedPolicies.length &&
						keyFactFilters.selectedKeyFactEndDate &&
						keyFactFilters.selectedKeyFactStartDate
					) {
						debouncedSaveChanges();
					}
					break;
				case KeyDateKind.Anniversary:
				case KeyDateKind.Birthday:
				case KeyDateKind.FinancialReview:
					if (keyFactFilters.selectedKeyFactEndDate && keyFactFilters.selectedKeyFactStartDate) {
						debouncedSaveChanges();
					}
					break;
				default:
					break;
			}
		}
	}, [
		debouncedSaveChanges,
		keyFactFilters.selectedKeyFact,
		keyFactFilters.selectedKeyFactEndDate,
		keyFactFilters.selectedKeyFactStartDate,
		keyFactFilters.selectedMaxAge,
		keyFactFilters.selectedMinAge,
		keyFactFilters.selectedPolicies,
	]);

	const handleClearFilters = () => {
		createDateFilters.refreshDateFilter(DefaultPeopleFilter?.filter?.criteria);
		lastCommunicationDateFilters.refreshDateFilter(DefaultPeopleFilter?.filter?.criteria);
		tagFilters.refreshTags(DefaultPeopleFilter?.filter?.criteria);
		keyFactFilters.refreshKeyFacts(DefaultPeopleFilter?.filter?.criteria);
		locationFilters.clearLocationFilters();
		companyTagFilters.clearCompanyTagFilters();
		onClear?.();
	};

	return (
		<Column styles={[styleSheet.drawerContainer]} scrollable>
			<fieldset className={css(styleSheet.fieldset)}>
				<legend className={css(styleSheet.legend, showPopularTags && styleSheet.paddingBottomMd)}>
					<ButtonBase
						className={css(styleSheet.row, styleSheet.collapseTrigger)}
						onClick={() => setShowPopularTags(prev => !prev)}
					>
						Popular Tags
						<RightChevron height={14} className={css(showPopularTags ? styleSheet.open : styleSheet.closed)} />
					</ButtonBase>
				</legend>
				<PopularTagsV2
					filter={filter}
					tags={tagFilters.orTags}
					onTagAdded={value => handleLiveUpdate(() => tagFilters.addOrTag(value))}
					onTagRemoved={value => handleLiveUpdate(() => tagFilters.removeOrTag(value))}
					className={css(!showPopularTags && styleSheet.hide)}
				/>
			</fieldset>
			<div className={css(styleSheet.divider)} />
			<fieldset className={css(styleSheet.fieldset)}>
				<legend className={css(styleSheet.legend, showSavedSearches && styleSheet.paddingBottomMd)}>
					<ButtonBase
						className={css(styleSheet.row, styleSheet.collapseTrigger)}
						onClick={() => setShowSavedSearches(prev => !prev)}
					>
						Saved Searches
						<RightChevron height={14} className={css(showSavedSearches ? styleSheet.open : styleSheet.closed)} />
					</ButtonBase>
				</legend>
				<SavedSearches
					filter={filter}
					onChangeFilter={handleLoadSavedSearch}
					className={css(!showSavedSearches && styleSheet.hide)}
				/>
			</fieldset>
			<div className={css(styleSheet.divider)} />
			<fieldset className={css(styleSheet.fieldset)}>
				<legend className={css(styleSheet.legend, showTagFilters && styleSheet.paddingBottomMd)}>
					<ButtonBase
						className={css(styleSheet.row, styleSheet.collapseTrigger)}
						onClick={() => setShowTagFilters(prev => !prev)}
					>
						Tags
						<RightChevron height={14} className={css(showTagFilters ? styleSheet.open : styleSheet.closed)} />
					</ButtonBase>
				</legend>
				<Column styles={[compoundFiltersStyles.gap, !showTagFilters && styleSheet.hide]}>
					<p className={css(compoundFiltersStyles.textBase)}>Included tag(s):</p>
					<TagListBuilder
						styles={[compoundFiltersStyles.tagListBuilderContainer]}
						inputId='add-or-tags'
						onTagAdded={value => handleLiveUpdate(() => tagFilters.addOrTag(value))}
						onTagRemoved={value => handleLiveUpdate(() => tagFilters.removeOrTag(value))}
						separator={FilterOperator.Or}
						tags={tagFilters.orTags}
						openOnFocus={true}
					/>
					<div />
					<p className={css(compoundFiltersStyles.textBase)}>Narrow the list:</p>
					<Checkbox checked={tagFilters.showingAndTags} id='and-tags' onChange={tagFilters.handleAndCheckChange}>
						<span className={css(compoundFiltersStyles.textSm, compoundFiltersStyles.textColor)}>
							Contacts must also have these tags...
						</span>
					</Checkbox>
					{tagFilters.showingAndTags ? (
						<TagListBuilder
							styles={[compoundFiltersStyles.tagListBuilderContainer]}
							inputId='add-and-tags'
							onTagAdded={value => handleLiveUpdate(() => tagFilters.addAndTag(value))}
							onTagRemoved={value => handleLiveUpdate(() => tagFilters.removeAndTag(value))}
							separator={FilterOperator.And}
							tags={tagFilters.andTags}
							openOnFocus={true}
						/>
					) : null}
					<Checkbox checked={tagFilters.showingNotTags} id='not-tags' onChange={tagFilters.handleNotCheckChange}>
						<span className={css(compoundFiltersStyles.textSm, compoundFiltersStyles.textColor)}>
							Exclude these tags...
						</span>
					</Checkbox>
					{tagFilters.showingNotTags ? (
						<TagListBuilder
							styles={[compoundFiltersStyles.tagListBuilderContainer]}
							inputId='add-not-tags'
							onTagAdded={value => handleLiveUpdate(() => tagFilters.addNotTag(value))}
							onTagRemoved={value => handleLiveUpdate(() => tagFilters.removeNotTag(value))}
							separator={FilterOperator.Not}
							tags={tagFilters.filteredNotTags}
							openOnFocus={true}
						/>
					) : null}
				</Column>
			</fieldset>
			<div className={css(styleSheet.divider)} />
			<fieldset className={css(styleSheet.fieldset)}>
				<legend className={css(styleSheet.legend, showOwnershipFilters && styleSheet.paddingBottomMd)}>
					<ButtonBase
						className={css(styleSheet.row, styleSheet.collapseTrigger)}
						onClick={() => setShowOwnershipFilters(prev => !prev)}
					>
						Ownership
						<RightChevron height={14} className={css(showOwnershipFilters ? styleSheet.open : styleSheet.closed)} />
					</ButtonBase>
				</legend>
				<OwnershipFilters
					filter={filter}
					onChangeFilter={handleOwnershipFilterChange}
					className={css(!showOwnershipFilters && styleSheet.hide)}
				/>
			</fieldset>
			<div className={css(styleSheet.divider)} />
			<fieldset className={css(styleSheet.fieldset)}>
				<legend className={css(styleSheet.legend, showStatusFilters && styleSheet.paddingBottomMd)}>
					<ButtonBase
						className={css(styleSheet.row, styleSheet.collapseTrigger)}
						onClick={() => setShowStatusFilters(prev => !prev)}
					>
						Status
						<RightChevron height={14} className={css(showStatusFilters ? styleSheet.open : styleSheet.closed)} />
					</ButtonBase>
				</legend>
				<StatusFilterCheckboxes
					filter={filter}
					onChangeFilter={handleStatusFilterChange}
					styles={[styleSheet.statusFilters, !showStatusFilters && styleSheet.hide]}
				/>
			</fieldset>
			<div className={css(styleSheet.divider)} />
			<fieldset className={css(styleSheet.fieldset)}>
				<legend className={css(styleSheet.legend, showLocationFilters && styleSheet.paddingBottomMd)}>
					<ButtonBase
						className={css(styleSheet.row, styleSheet.collapseTrigger)}
						onClick={() => setShowLocationFilters(prev => !prev)}
					>
						Location
						<RightChevron height={14} className={css(showLocationFilters ? styleSheet.open : styleSheet.closed)} />
					</ButtonBase>
				</legend>
				<LocationFilters
					filter={filter}
					onChangeFilter={handleLocationFilterChange}
					styles={[!showLocationFilters && styleSheet.hide]}
				/>
			</fieldset>
			<div className={css(styleSheet.divider)} />
			<fieldset className={css(styleSheet.fieldset)}>
				<legend className={css(styleSheet.legend, showCompanyTagFilters && styleSheet.paddingBottomMd)}>
					<ButtonBase
						className={css(styleSheet.row, styleSheet.collapseTrigger)}
						onClick={() => setShowCompanyTagFilters(prev => !prev)}
					>
						Company Tags
						<RightChevron height={14} className={css(showCompanyTagFilters ? styleSheet.open : styleSheet.closed)} />
					</ButtonBase>
				</legend>
				<CompanyTagFilters
					filter={filter}
					onChangeFilter={handleCompanyTagFilterChange}
					styles={[!showCompanyTagFilters && styleSheet.hide]}
				/>
			</fieldset>
			<div className={css(styleSheet.divider)} />
			<fieldset className={css(styleSheet.fieldset)}>
				<legend className={css(styleSheet.legend, showCustomDateRange && styleSheet.paddingBottomMd)}>
					<ButtonBase
						className={css(styleSheet.row, styleSheet.collapseTrigger)}
						onClick={() => setShowCustomDateRange(prev => !prev)}
					>
						Contact Creation Date
						<RightChevron height={14} className={css(showCustomDateRange ? styleSheet.open : styleSheet.closed)} />
					</ButtonBase>
				</legend>
				<span className={css(!showCustomDateRange && styleSheet.hide)}>
					<Popover>
						<div>
							<ButtonGroup styles={[styleSheet.dateButtonGroup]}>
								<PopoverTrigger asChild>
									<Button
										variant='outline'
										id='dates'
										styles={[
											styleSheet.dateButton,
											createDateFilters.startDate || createDateFilters.endDate ? styleSheet.dateButtonActive : null,
										]}
									>
										{createDateFilters.startDate && !createDateFilters.endDate
											? `${createDateFilters.startDate.toLocaleDateString()}`
											: createDateFilters.startDate && createDateFilters.endDate
												? `${createDateFilters.startDate.toLocaleDateString()} - ${createDateFilters.endDate.toLocaleDateString()}`
												: 'Filter by Contact Creation Date'}
										{!createDateFilters.startDate && !createDateFilters.endDate ? <ChevronDown /> : null}
									</Button>
								</PopoverTrigger>
								{createDateFilters.startDate || createDateFilters.endDate ? (
									<Button
										variant='outline'
										onClick={() => {
											createDateFilters.refreshDateFilter([]);
											debouncedSaveChanges();
										}}
									>
										<XIconNew />
									</Button>
								) : null}
							</ButtonGroup>
						</div>
						<PopoverContent styles={[bs.wAuto, bs.overflowHidden, bs.p0]} align='start'>
							<Calendar
								mode='range'
								selected={{
									from: createDateFilters.startDate || null,
									to: createDateFilters.endDate || createDateFilters.startDate || null,
								}}
								numberOfMonths={2}
								onSelect={r => {
									handleLiveUpdate(() => {
										if (!r) {
											createDateFilters.refreshDateFilter([]);
											debouncedSaveChanges();
											return;
										}

										createDateFilters.setStartDate(r?.from);
										const fromMoment = moment(r?.from);
										const toMoment = moment(r?.to);

										if (!r?.to || toMoment.isSame(fromMoment, 'day')) {
											createDateFilters.setEndDate(null);
											return;
										}

										createDateFilters.setEndDate(r?.to);
									});
								}}
							/>
						</PopoverContent>
					</Popover>
				</span>
			</fieldset>
			<div className={css(styleSheet.divider)} />
			<fieldset className={css(styleSheet.fieldset)}>
				<legend className={css(styleSheet.legend, showLastCommunicationDateRange && styleSheet.paddingBottomMd)}>
					<ButtonBase
						className={css(styleSheet.row, styleSheet.collapseTrigger)}
						onClick={() => setShowLastCommunicationDateRange(prev => !prev)}
					>
						Last Communication
						<RightChevron
							height={14}
							className={css(showLastCommunicationDateRange ? styleSheet.open : styleSheet.closed)}
						/>
					</ButtonBase>
				</legend>
				<span className={css(!showLastCommunicationDateRange && styleSheet.hide)}>
					<LastCommunicationDateDropdown
						saveChanges={debouncedSaveChanges}
						dateRangeFilters={lastCommunicationDateFilters}
					/>
				</span>
			</fieldset>
			<div className={css(styleSheet.divider)} />
			<fieldset className={css(styleSheet.fieldset)}>
				<legend className={css(styleSheet.legend, showKeyFactFilters && styleSheet.paddingBottomMd)}>
					<ButtonBase
						className={css(styleSheet.row, styleSheet.collapseTrigger)}
						onClick={() => setShowKeyFactFilters(prev => !prev)}
					>
						Key Fact
						<RightChevron height={14} className={css(showKeyFactFilters ? styleSheet.open : styleSheet.closed)} />
					</ButtonBase>
				</legend>
				<KeyFactFiltersDropdown
					className={css(!showKeyFactFilters && styleSheet.hide)}
					selectedKeyFact={keyFactFilters.selectedKeyFact}
					setSelectedKeyFact={keyFactFilters.setSelectedKeyFact}
					selectedPolicies={keyFactFilters.selectedPolicies}
					setSelectedPolicies={keyFactFilters.setSelectedPolicies}
					selectedKeyFactStartDate={keyFactFilters.selectedKeyFactStartDate}
					setSelectedKeyFactStartDate={keyFactFilters.setSelectedKeyFactStartDate}
					selectedKeyFactEndDate={keyFactFilters.selectedKeyFactEndDate}
					setSelectedKeyFactEndDate={keyFactFilters.setSelectedKeyFactEndDate}
					selectedMinAge={keyFactFilters.selectedMinAge}
					setSelectedMinAge={keyFactFilters.setSelectedMinAge}
					selectedMaxAge={keyFactFilters.selectedMaxAge}
					setSelectedMaxAge={keyFactFilters.setSelectedMaxAge}
					keyFactErrors={keyFactFilters.keyFactErrors}
					styles={[compoundFiltersStyles.policySelectConstraint]}
				/>
			</fieldset>
			<div className={css(styleSheet.divider)} />
			<Button variant='link' onClick={handleClearFilters}>
				Clear all filters
			</Button>
		</Column>
	);
};
